<template>
    <div id="dmn">
        <div
            id="dmn-js"
            ref="dmnJsElement"
            :class="{ 'editing': isEditing }"
            :style="`height:${height}px;`"
        />
    </div>
</template>

<script setup lang="ts">
import {
    ref,
    onMounted,
    onUnmounted,
    watch,
    markRaw,
    useTemplateRef,
} from "vue";
import { exists } from "@/utils";
import baseDMN from "@/assets/base.dmn?raw";
import { Artefact } from "@/lib/artefacts";
// @ts-expect-error: Package doesn't have types.
import DmnJs from "dmn-js/lib/Modeler";
// @ts-expect-error: Package doesn't have types.
import DmnJsViewer from "dmn-js/lib/NavigatedViewer";

const model = defineModel<Artefact>({ required: true });

const props = withDefaults(
    defineProps<{
        height?: number;
        zoomScroll?: boolean;
        isEditing?: boolean;
    }>(),
    {
        height: 750,
        zoomScroll: false,
        isEditing: false,
    }
);

const currentView = ref();
const xml = ref<string>();

const emit = defineEmits<{
    (e: "updating", value: boolean): void;
    (e: "changed"): void;
    (e: "element-selected"): void;
}>();

const dmnJs = ref<InstanceType<typeof DmnJs>>(
    markRaw({} as InstanceType<typeof DmnJs>)
);
const dmnJsRef = useTemplateRef<HTMLElement>("dmnJsElement");

const addEventListenersToViewer = (viewer: DmnJs) => {
    viewer.on("commandStack.changed", async () => {
        emit("updating", true);
        emit("changed");
        const { xml: xml_ } = await dmnJs.value.saveXML({ format: true });
        xml.value = xml_;
        model.value.content = xml_;
        emit("updating", false);
    });
};

const addDMNEventListeners = () => {
    if (props.isEditing) {
        // Add eventListeners to viewers which already exist
        dmnJs.value.getViews().forEach((view) => {
            const viewer = dmnJs.value._getViewer(view);
            addEventListenersToViewer(viewer);
        });

        // Add eventListeners to newly created viewers
        dmnJs.value.on("viewer.created", ({ viewer }: { viewer: DmnJs }) => {
            addEventListenersToViewer(viewer);
        });
    }

    dmnJs.value.on("views.changed", ({ activeView }) => {
        currentView.value = activeView;
    });
};

const loadDMN = async () => {
    try {
        const result = await dmnJs.value.importXML(
            model.value.content || baseDMN
        );
        const { warnings } = result;
        if (warnings?.length) {
            console.warn(warnings);
        }
    } catch (err) {
        console.warn(err.message, err.warnings);
    }
};

const initDmnJs = async () => {
    let viewBox, activeView;
    if (exists(dmnJs.value)) {
        activeView = dmnJs.value?.getActiveView();
        try {
            const canvas = dmnJs.value?.getActiveViewer()?.get("canvas");
            viewBox = canvas?.viewbox();
        } catch {
            // No canvas, e.g. for tables.
        }
        dmnJs.value.detach();
    }

    if (props.isEditing) {
        // Create dmn-js editor
        dmnJs.value = markRaw(
            new DmnJs({
                zoomScroll: {
                    "enabled": true,
                    "scale": 0.3,
                },
                keyboard: { bindTo: document },
            })
        );
    } else {
        // Create dmn-js viewer
        dmnJs.value = markRaw(
            new DmnJsViewer({
                zoomScroll: {
                    "enabled": props.zoomScroll,
                    "scale": 0.3,
                },
            })
        );
    }
    dmnJs.value.attachTo(dmnJsRef.value);
    await loadDMN();
    addDMNEventListeners();
    if (activeView) {
        // Find view by ID so we have the correc references.
        const view = dmnJs.value
            .getViews()
            .find(({ id }) => id === activeView.id);
        dmnJs.value.open(view);
    }
    try {
        const canvas = dmnJs.value?.getActiveViewer()?.get("canvas");
        if (viewBox) {
            canvas?.viewbox(viewBox);
        } else {
            canvas?.zoom("fit-viewport");
        }
    } catch {
        // No canvas, e.g. for tables.
    }
};

// Exposed method for external use
const getAsXMLFile = async (filename: string): Promise<File> => {
    if (!filename.endsWith(".dmn")) {
        filename += ".dmn";
    }
    const { xml } = await dmnJs.value.saveXML({ format: true });
    return new File([xml], filename, {
        type: "application/dmn+xml",
        endings: "transparent",
    });
};

// Watchers
watch(
    () => model.value.content,
    (content) => {
        if (content && content !== xml.value) {
            loadDMN();
        }
    }
);

watch(
    () => props.zoomScroll,
    (newValue) => {
        if (!props.isEditing) {
            const zoomScroll = dmnJs.value.get("zoomScroll");
            zoomScroll.toggle(newValue);
        }
    }
);

watch(
    () => props.isEditing,
    (newValue, oldValue) => {
        if (newValue !== oldValue) {
            initDmnJs();
        }
    }
);

// Lifecycle hooks
onMounted(() => {
    initDmnJs();
});

onUnmounted(() => {
    dmnJs.value.destroy();
});

defineExpose({
    getAsXMLFile,
});
</script>

<style lang="sass" scoped>
:deep(#object-diagram-modeler)
    &.editing
        border: 2px solid var(--stekz-blue-darkest)
    &:not(.editing) .djs-element
        /* Selected task (rect), gateway (polygon) or start/end/error (circle) */
        &.selected
            .djs-outline
                stroke: var(--stekz-blue-darkest)
                stroke-width: 2px

        &.hover
            .djs-outline
                stroke: var(--stekz-blue-lightest)
</style>
<style src="dmn-js/dist/assets/diagram-js.css"></style>
<style src="dmn-js/dist/assets/dmn-js-shared.css"></style>
<style src="dmn-js/dist/assets/dmn-js-drd.css"></style>
<style src="dmn-js/dist/assets/dmn-js-decision-table.css"></style>
<style src="dmn-js/dist/assets/dmn-js-decision-table-controls.css"></style>
<style src="dmn-js/dist/assets/dmn-js-literal-expression.css"></style>
<style src="dmn-js/dist/assets/dmn-font/css/dmn.css"></style>
